import { DEFAULT_PRODUCT_BRAND_NAME } from '@terra/shared-utils';

export const PDF_TEMPLATE_IDS = {
  DEFAULT: 'default-template',
  EXTERNAL_NETWORK_PENTEST: 'external-network-pentest-template',
  LETTER_OF_ATTESTATION: 'letter-of-attestation-template',
  EXECUTIVE_SUMMARY: 'executive-summary-template',
} as const;

export type PdfTemplateId =
  (typeof PDF_TEMPLATE_IDS)[keyof typeof PDF_TEMPLATE_IDS];

export const PDF_ASSET_PATHS = {
  COVER: '/assets/pdf/cover.png',
  BACK_COVER: '/assets/pdf/back-cover.png',
  SCHEDULED_COVER: '/assets/pdf/scheduled-cover.png',
} as const;

export const PRODUCT_COVER_ASSET_URLS: readonly string[] = [
  PDF_ASSET_PATHS.COVER,
  PDF_ASSET_PATHS.BACK_COVER,
  PDF_ASSET_PATHS.SCHEDULED_COVER,
];

export const PDF_LAYOUT_SECTIONS = {
  TABLE_OF_CONTENTS: 'table-of-contents',
  EXECUTIVE_SUMMARY: 'executive-summary',
  STRATEGIC_NEXT_STEPS: 'strategic-next-steps',
  SCOPING: 'scoping',
  DISCOVERY: 'discovery',
  METHODOLOGY: 'methodology',
  FINDINGS_SUMMARY: 'findings-summary',
  FINDINGS: 'findings',
  FINDINGS_BY_APPLICATION: 'findings-by-application',
  APPENDIX_RISK_RATINGS: 'appendix-risk-ratings',
  APPENDIX_TOOLS: 'appendix-tools',
  DISCOVERY_APPENDIX: 'discovery-appendix',
  CLIENT_INFORMATION: 'client-information',
} as const;

export type PdfLayoutSection =
  (typeof PDF_LAYOUT_SECTIONS)[keyof typeof PDF_LAYOUT_SECTIONS];

export const PDF_EXECUTIVE_SUMMARY_KINDS = {
  FULL: 'full',
  ATTESTATION: 'attestation',
  MULTI_APP: 'multi-app',
} as const;

export type PdfExecutiveSummaryKind =
  (typeof PDF_EXECUTIVE_SUMMARY_KINDS)[keyof typeof PDF_EXECUTIVE_SUMMARY_KINDS];

export const PDF_STANDARD_REPORT_SECTIONS: readonly PdfLayoutSection[] = [
  PDF_LAYOUT_SECTIONS.TABLE_OF_CONTENTS,
  PDF_LAYOUT_SECTIONS.EXECUTIVE_SUMMARY,
  PDF_LAYOUT_SECTIONS.STRATEGIC_NEXT_STEPS,
  PDF_LAYOUT_SECTIONS.SCOPING,
  PDF_LAYOUT_SECTIONS.DISCOVERY,
  PDF_LAYOUT_SECTIONS.METHODOLOGY,
  PDF_LAYOUT_SECTIONS.FINDINGS_SUMMARY,
  PDF_LAYOUT_SECTIONS.FINDINGS,
  PDF_LAYOUT_SECTIONS.APPENDIX_RISK_RATINGS,
  PDF_LAYOUT_SECTIONS.APPENDIX_TOOLS,
  PDF_LAYOUT_SECTIONS.DISCOVERY_APPENDIX,
  PDF_LAYOUT_SECTIONS.CLIENT_INFORMATION,
];

export const PDF_LETTER_OF_ATTESTATION_SECTIONS: readonly PdfLayoutSection[] = [
  PDF_LAYOUT_SECTIONS.EXECUTIVE_SUMMARY,
  PDF_LAYOUT_SECTIONS.SCOPING,
  PDF_LAYOUT_SECTIONS.CLIENT_INFORMATION,
];

export const PDF_EXECUTIVE_SUMMARY_REPORT_SECTIONS: readonly PdfLayoutSection[] =
  [
    PDF_LAYOUT_SECTIONS.EXECUTIVE_SUMMARY,
    PDF_LAYOUT_SECTIONS.SCOPING,
    PDF_LAYOUT_SECTIONS.FINDINGS_BY_APPLICATION,
    PDF_LAYOUT_SECTIONS.CLIENT_INFORMATION,
  ];

export type PdfRenderProfile = {
  id: string;
  name: string;
  reportTypeLabel: string;
  reportTargetLabel?: string;
  methodologyVariant?: 'web-application' | 'external-network';
  coverImageUrl: string;
  backCoverImageUrl: string;
  sections: readonly PdfLayoutSection[];
  executiveSummaryKind: PdfExecutiveSummaryKind;
};

export const PDF_LETTER_OF_ATTESTATION_NAME = 'Letter of Attestation';
export const PDF_EXECUTIVE_SUMMARY_NAME = 'Executive Summary';

const PDF_RENDER_PROFILES: Record<string, PdfRenderProfile> = {
  [PDF_TEMPLATE_IDS.DEFAULT]: {
    id: PDF_TEMPLATE_IDS.DEFAULT,
    name: DEFAULT_PRODUCT_BRAND_NAME,
    reportTypeLabel: 'Security Assessment Report',
    reportTargetLabel: 'application',
    methodologyVariant: 'web-application',
    coverImageUrl: PDF_ASSET_PATHS.COVER,
    backCoverImageUrl: PDF_ASSET_PATHS.BACK_COVER,
    sections: PDF_STANDARD_REPORT_SECTIONS,
    executiveSummaryKind: PDF_EXECUTIVE_SUMMARY_KINDS.FULL,
  },
  [PDF_TEMPLATE_IDS.EXTERNAL_NETWORK_PENTEST]: {
    id: PDF_TEMPLATE_IDS.EXTERNAL_NETWORK_PENTEST,
    name: 'External Network Pentest Report',
    reportTypeLabel: 'External Network Pentest Report',
    reportTargetLabel: 'external network',
    methodologyVariant: 'external-network',
    coverImageUrl: PDF_ASSET_PATHS.COVER,
    backCoverImageUrl: PDF_ASSET_PATHS.BACK_COVER,
    sections: PDF_STANDARD_REPORT_SECTIONS,
    executiveSummaryKind: PDF_EXECUTIVE_SUMMARY_KINDS.FULL,
  },
  [PDF_TEMPLATE_IDS.LETTER_OF_ATTESTATION]: {
    id: PDF_TEMPLATE_IDS.LETTER_OF_ATTESTATION,
    name: PDF_LETTER_OF_ATTESTATION_NAME,
    reportTypeLabel: PDF_LETTER_OF_ATTESTATION_NAME,
    reportTargetLabel: 'application',
    methodologyVariant: 'web-application',
    coverImageUrl: PDF_ASSET_PATHS.COVER,
    backCoverImageUrl: PDF_ASSET_PATHS.BACK_COVER,
    sections: PDF_LETTER_OF_ATTESTATION_SECTIONS,
    executiveSummaryKind: PDF_EXECUTIVE_SUMMARY_KINDS.ATTESTATION,
  },
  [PDF_TEMPLATE_IDS.EXECUTIVE_SUMMARY]: {
    id: PDF_TEMPLATE_IDS.EXECUTIVE_SUMMARY,
    name: PDF_EXECUTIVE_SUMMARY_NAME,
    reportTypeLabel: 'Security Assessment Report',
    reportTargetLabel: 'applications',
    methodologyVariant: 'web-application',
    coverImageUrl: PDF_ASSET_PATHS.COVER,
    backCoverImageUrl: PDF_ASSET_PATHS.BACK_COVER,
    sections: PDF_EXECUTIVE_SUMMARY_REPORT_SECTIONS,
    executiveSummaryKind: PDF_EXECUTIVE_SUMMARY_KINDS.MULTI_APP,
  },
};

export function resolvePdfRenderProfile({
  templateId,
  isExternalNetwork,
}: {
  templateId?: string;
  isExternalNetwork: boolean;
}): PdfRenderProfile {
  if (templateId === PDF_TEMPLATE_IDS.EXECUTIVE_SUMMARY) {
    return PDF_RENDER_PROFILES[PDF_TEMPLATE_IDS.EXECUTIVE_SUMMARY];
  }

  if (templateId === PDF_TEMPLATE_IDS.LETTER_OF_ATTESTATION) {
    const profile = PDF_RENDER_PROFILES[PDF_TEMPLATE_IDS.LETTER_OF_ATTESTATION];

    if (isExternalNetwork) {
      return {
        ...profile,
        reportTargetLabel: 'external network',
        methodologyVariant: 'external-network',
      };
    }

    return profile;
  }

  if (
    templateId === PDF_TEMPLATE_IDS.EXTERNAL_NETWORK_PENTEST ||
    isExternalNetwork
  ) {
    return PDF_RENDER_PROFILES[PDF_TEMPLATE_IDS.EXTERNAL_NETWORK_PENTEST];
  }

  if (templateId && PDF_RENDER_PROFILES[templateId]) {
    return PDF_RENDER_PROFILES[templateId];
  }

  return PDF_RENDER_PROFILES[PDF_TEMPLATE_IDS.DEFAULT];
}

export function pdfProfileIncludesSection({
  sections,
  section,
}: {
  sections: readonly PdfLayoutSection[];
  section: PdfLayoutSection;
}): boolean {
  return sections.includes(section);
}
